import { Button, Form } from "react-bootstrap";
import { Fragment, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { AttributesForm, ImagesForm, LoadingSpinner } from "../../components";

export default function ProductForm(props) {
  // form for creating/editing/deleting a product
  // allows image uploads and previews

  // form validation with Yup
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Please enter a product name"),
    description: Yup.string().required("Please enter a product description"),
    inventoryCount: Yup.number()
      .integer("Count cannot be a decimal")
      .min(0)
      .required("Please enter a count greater or equal to 0"),
    price: Yup.number()
      .min(0)
      .required("Please enter a price greater or equal to 0"),
    category: Yup.string()
      .required("Please select a category")
      .typeError("Please select a category"),
    images: Yup.array()
      .min(1, "Please select at least 1 image")
      .max(5, "You can upload up to 5 images"),
  });

  const simpleFormFields = [
    { name: "name", type: "text", label: "Name" },
    {
      name: "description",
      type: "textarea",
      rows: 3,
      maxLength: 400,
      label: "Description",
    },
    { name: "price", type: "number", step: "0.01", min: 0, label: "Price" },
    {
      name: "inventoryCount",
      type: "number",
      step: "1",
      min: 0,
      label: "Inventory Count",
    },
  ];

  const [disableSave, setDisableSave] = useState(false);
  const [attributes, setAttributes] = useState(
    props?.productData?.attributes || []
  ); //already existing ones for this product(from attributes collection)
  const [newCustomAttributes, setNewCustomAttributes] = useState([]); //new, not yet in attributes

  const renderCategoryDropdown = (categories) => {
    return categories.map((category, idx) => {
      return (
        <Fragment key={category?._id}>
          <option value={category._id}>{category?.fullPath}</option>
          {category.children && category.children.length > 0 && (
            <>{renderCategoryDropdown(category.children)}</>
          )}
        </Fragment>
      );
    });
  };

  return (
    <Formik
      initialValues={{
        name: props?.productData?.name || "",
        description: props?.productData?.description || "",
        inventoryCount: props?.productData?.inventoryCount || "",
        price: props?.productData?.price || "",
        category:
          props?.productData?.categories?.[
            props.productData.categories.length - 1
          ]?._id || "",
        images: props?.productData?.images || [],
      }}
      enableReinitialize={true} //automatically update initialValues to initialProductData once fetched from DB
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        // save/update product with new data
        const productData = {
          name: values.name,
          description: values.description,
          inventoryCount: values.inventoryCount,
          price: values.price,
          category: values.category,
          attributes: attributes,
          customAttributes: newCustomAttributes,
        };
        await props.handleSave(productData);
        setSubmitting(false);
      }}
    >
      {({
        handleSubmit,
        handleChange,
        values,
        touched,
        errors,
        isSubmitting,
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          {/* FORM FIELDS */}
          {simpleFormFields.map((field, idx) => (
            <Form.Group key={idx} className="mb-3" controlId={field.name}>
              <Form.Label>{field.label}</Form.Label>
              <Form.Control
                name={field.name}
                type={field.type}
                value={values[field.name]}
                onChange={handleChange}
                isInvalid={touched[field.name] && !!errors[field.name]}
                rows={field?.rows || null}
                maxLength={field?.maxLength || null}
                step={field?.step || null}
                min={field?.min | 0}
              />
              <Form.Control.Feedback type="invalid">
                {errors[field.name]}
              </Form.Control.Feedback>
            </Form.Group>
          ))}

          <Form.Group className="mb-3" controlId="category">
            <Form.Label>Category</Form.Label>
            <Form.Select
              name="category"
              value={values.category}
              onChange={handleChange}
              isInvalid={touched.category && !!errors.category}
            >
              {!props.edit && <option value="">Choose a category</option>}
              {props.availableCategories &&
                renderCategoryDropdown(props.availableCategories)}
            </Form.Select>

            <Form.Control.Feedback type="invalid">
              {errors.category}
            </Form.Control.Feedback>
          </Form.Group>
          <AttributesForm
            availableAttributes={props.availableAttributes}
            newCustomAttributes={newCustomAttributes}
            setNewCustomAttributes={setNewCustomAttributes}
            attributes={attributes}
            setAttributes={setAttributes}
          />

          <ImagesForm
            handleChange={handleChange}
            images={values.images}
            handleAddImages={props.handleAddImages}
            setDisableSave={setDisableSave}
            handleDeleteImage={props.handleDeleteImage}
            edit={props.edit}
            touched={touched}
            errors={errors}
          />

          <Button
            type="submit"
            className="mt-2"
            variant="primary"
            disabled={disableSave}
          >
            {isSubmitting && <LoadingSpinner button={true} variant="light" />}{" "}
            {props.edit ? "Save Changes" : "Save Product"}
          </Button>
          <Button
            className="mt-2 ms-3"
            variant="danger"
            type="button"
            onClick={() => {
              if (props.edit) {
                props.handleDeleteProduct(props.productData?.id);
              } else {
                props.handleCancel();
              }
            }}
          >
            {props.edit ? "Delete Product" : "Cancel"}
          </Button>
        </Form>
      )}
    </Formik>
  );
}
